import React from "react";

/**
 * Utility functions for timezone-aware date handling
 */
export class TimezoneUtils {
    /**
     * Get the user's timezone from the browser
     */
    static getUserTimezone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    /**
     * Get a short timezone abbreviation (e.g., PST, EST, UTC+2)
     */
    static getTimezoneAbbreviation(): string {
        const timezone = this.getUserTimezone();
        const now = new Date();

        try {
            // Try to get the timezone abbreviation
            const formatter = new Intl.DateTimeFormat("en", {
                timeZone: timezone,
                timeZoneName: "short"
            });

            const parts = formatter.formatToParts(now);
            const timeZonePart = parts.find(part => part.type === "timeZoneName");

            if (timeZonePart) {
                return timeZonePart.value;
            }
        } catch (error) {
            // Fallback to manual calculation
        }

        // Fallback: show offset
        const offset = now.getTimezoneOffset();
        const sign = offset <= 0 ? "+" : "-";
        const hours = Math.floor(Math.abs(offset) / 60);
        const minutes = Math.abs(offset) % 60;

        if (minutes === 0) {
            return `UTC${sign}${hours}`;
        } else {
            return `UTC${sign}${hours}:${minutes.toString().padStart(2, "0")}`;
        }
    }

    /**
     * Convert a timestamp (milliseconds since epoch) to user's timezone
     */
    static formatTimestamp(timestamp: number, options: Intl.DateTimeFormatOptions = {}): string {
        const date = new Date(timestamp);
        const timezone = this.getUserTimezone();

        const defaultOptions: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZone: timezone,
            ...options
        };

        return new Intl.DateTimeFormat("en-US", defaultOptions).format(date);
    }

    /**
     * Convert a timestamp to a localized date string (without time)
     */
    static formatDate(timestamp: number, options: Intl.DateTimeFormatOptions = {}): string {
        const date = new Date(timestamp);
        const timezone = this.getUserTimezone();

        const defaultOptions: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "short",
            day: "numeric",
            timeZone: timezone,
            ...options
        };

        return new Intl.DateTimeFormat("en-US", defaultOptions).format(date);
    }

    /**
     * Convert a timestamp to a localized time string (without date)
     */
    static formatTime(timestamp: number, options: Intl.DateTimeFormatOptions = {}): string {
        const date = new Date(timestamp);
        const timezone = this.getUserTimezone();

        const defaultOptions: Intl.DateTimeFormatOptions = {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZone: timezone,
            ...options
        };

        return new Intl.DateTimeFormat("en-US", defaultOptions).format(date);
    }

    /**
     * Format a date range for charts with timezone awareness
     */
    static formatDateRange([start, end]: [Date, Date]): string {
        const timezone = this.getUserTimezone();
        const options: Intl.DateTimeFormatOptions = {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: timezone
        };

        const startFormatted = start.toLocaleDateString("en-US", options);
        const endFormatted = end.toLocaleDateString("en-US", options);

        return `${startFormatted} - ${endFormatted}`;
    }

    /**
     * Convert a Date object to YYYY-MM-DD format in the user's timezone
     */
    static formatDateForAPI(date: Date): string {
        const timezone = this.getUserTimezone();
        const options: Intl.DateTimeFormatOptions = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            timeZone: timezone
        };

        // Get the date parts in user timezone
        const formatter = new Intl.DateTimeFormat("en-CA", options); // en-CA gives YYYY-MM-DD format
        return formatter.format(date);
    }
}
